import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaDownload } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { API_HOST } from '../../../../../config/api.js';

import GlobalHeader from '../../../../../components/GlobalHeader.jsx';
import Pagination from '../../../../../components/Pagination.jsx';
import '../css/TicketsRequest.css';

const API_URL = API_HOST;
const ITEMS_PER_PAGE = 5;

const TicketsRequest = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [requests, setRequests] = useState([]);
  const [eventName, setEventName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadMessage, setDownloadMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No authentication token found. Please log in.');
          setLoading(false);
          navigate('/');
          return;
        }

        console.log('[TicketsRequest] 🎫 Fetching — API: GET /api/organiser/events/:eventId/ticket-requests', { eventId });
        const response = await fetch(
          `${API_URL}/api/organiser/events/${eventId}/ticket-requests`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('[TicketsRequest] ✅ Ticket requests loaded:', {
          columns: 'purchase_id, purchaser_name, package, number_of_tickets, amount, ticket_number, delegate_details[{name,email,phone,title,gender,dayPass,delegation,ieeeNumber}], event_name',
          count: data?.length,
        });
        setRequests(data);

        // Fetch the event name from the organiser events endpoint using eventId
        try {
          const eventResponse = await fetch(
            `${API_URL}/api/organiser/events/${eventId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (eventResponse.ok) {
            const eventData = await eventResponse.json();
            setEventName(eventData.event_name || eventData.name || 'Event');
          } else {
            // Fallback: try to get name from ticket requests data if available
            setEventName(
              data.length > 0
                ? data[0].event_name || data[0].name || 'Event'
                : 'Event'
            );
          }
        } catch (nameErr) {
          console.error('Error fetching event name:', nameErr);
          setEventName(
            data.length > 0
              ? data[0].event_name || data[0].name || 'Event'
              : 'Event'
          );
        }
      } catch (err) {
        console.error('Error fetching requests:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [eventId, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleDownloadCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please log in.');
        navigate('/');
        return;
      }

      const response = await fetch(
        `${API_URL}/api/organiser/events/${eventId}/ticket-requests?all=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.length === 0) {
        setDownloadMessage('No customer data available to download.');
        setTimeout(() => setDownloadMessage(''), 3000);
        return;
      }

      const excelRows = data.map((request) => {
        let delegate = {};
        const rawDelegate = request.delegate_details;

        if (rawDelegate) {
          if (typeof rawDelegate === 'string') {
            try {
              delegate = JSON.parse(rawDelegate);
            } catch (e) {
              delegate = {};
            }
          } else if (typeof rawDelegate === 'object') {
            delegate = rawDelegate;
          }
        }

        return {
          'Purchaser Name': request.purchaser_name,
          'Package Name': request.package,
          'Number of Tickets': request.number_of_tickets,
          Amount: `R ${Number(request.amount).toFixed(2)}`,
          'Ticket Number': request.ticket_number || '',
          Name: delegate.name || '',
          Email: delegate.email || '',
          Phone: delegate.phone || '',
          Title: delegate.title || '',
          Gender: delegate.gender || '',
          'Day Pass': delegate.dayPass || '',
          Delegation: delegate.delegation || '',
          'IEEE Number': delegate.ieeeNumber || '',
        };
      });

      const totalTickets = data.reduce(
        (sum, request) => sum + request.number_of_tickets,
        0
      );
      const totalAmount = data.reduce(
        (sum, request) => sum + Number(request.amount),
        0
      );

      excelRows.push({
        'Purchaser Name': 'Total',
        'Package Name': '',
        'Number of Tickets': totalTickets,
        Amount: `R ${totalAmount.toFixed(2)}`,
        'Ticket Number': '',
        Name: '',
        Email: '',
        Phone: '',
        Title: '',
        Gender: '',
        'Day Pass': '',
        Delegation: '',
        'IEEE Number': '',
      });

      const headers = [
        'Purchaser Name',
        'Package Name',
        'Number of Tickets',
        'Amount',
        'Ticket Number',
        'Name',
        'Email',
        'Phone',
        'Title',
        'Gender',
        'Day Pass',
        'Delegation',
        'IEEE Number',
      ];

      const aoa = [];

      // First row: event name as a heading (prefer state eventName)
      aoa.push([eventName || data[0]?.event_name || 'Event']);

      // Blank spacer row
      aoa.push([]);

      // Header row
      aoa.push(headers);

      // Data rows
      excelRows.forEach((row) => {
        aoa.push(headers.map((h) => row[h] ?? ''));
      });

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      ws['!cols'] = [
        { wch: 20 }, // Purchaser Name
        { wch: 20 }, // Package Name
        { wch: 18 }, // Number of Tickets
        { wch: 15 }, // Amount
        { wch: 20 }, // Ticket Number
        { wch: 20 }, // Delegate Name
        { wch: 28 }, // Delegate Email
        { wch: 16 }, // Delegate Phone
        { wch: 12 }, // Delegate Title
        { wch: 12 }, // Delegate Gender
        { wch: 14 }, // Delegate Day Pass
        { wch: 18 }, // Delegate Delegation
        { wch: 18 }, // Delegate IEEE Number
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        eventName || data[0]?.event_name || 'Customers'
      );
      XLSX.writeFile(wb, `${eventName || data[0]?.event_name || 'event'}_customers.xlsx`);
    } catch (err) {
      console.error('Error downloading customers:', err);
      alert('Failed to download customer list: ' + err.message);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const filteredRequests = useMemo(() => {
    if (!searchTerm) return requests;
    const term = searchTerm.toLowerCase();
    return requests.filter(
      (r) =>
        (r.purchaser_name || '').toLowerCase().includes(term) ||
        (r.package || '').toLowerCase().includes(term)
    );
  }, [requests, searchTerm]);

  const paginatedRequests = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredRequests.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredRequests, currentPage]);

  const hasRequests = filteredRequests && filteredRequests.length > 0;

  return (
    <div className="tickets-request-page">
      <GlobalHeader
        title="Tickets"
        subtitle={
          eventName ? `View tickets purchased for ${eventName}` : 'View tickets purchased'
        }
        backTo="/tickets-event-list"
        logoutTo="/"
        onLogout={handleLogout}
      />

      <main className="tickets-request-main">
        {loading && (
          <div className="tickets-request-loading">
            <div className="tickets-request-loading-spinner">⏳</div>
            <p>Loading ticket requests...</p>
          </div>
        )}

        {!loading && error && (
          <div className="tickets-request-error">
            <div className="tickets-request-error-icon">❌</div>
            <p className="tickets-request-error-text">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="tickets-request-button-container">
              <button
                className="tickets-request-download-button"
                onClick={handleDownloadCustomers}
              >
                <FaDownload className="tickets-request-button-icon" />
                Download All Customers
              </button>
              {downloadMessage && (
                <div className="tickets-request-download-message">
                  {downloadMessage}
                </div>
              )}
            </div>

            <div className="tickets-request-table-container">
              <div className="tickets-request-table-wrapper">
                <table className="tickets-request-table">
                  <thead>
                    <tr>
                      <th>Purchaser Name</th>
                      <th>Number of Tickets</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!hasRequests ? (
                      <tr>
                        <td colSpan="2" className="tickets-request-empty-state">
                          No ticket requests found
                        </td>
                      </tr>
                    ) : (
                      paginatedRequests.map((request) => (
                        <tr key={request.purchase_id} className="tickets-request-row">
                          <td className="tickets-request-cell">
                            {request.purchaser_name}
                          </td>
                          <td className="tickets-request-cell">
                            <div className="tickets-request-count-badge">
                              {request.number_of_tickets}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredRequests.length / ITEMS_PER_PAGE)}
                onPageChange={setCurrentPage}
                itemsPerPage={ITEMS_PER_PAGE}
                totalItems={filteredRequests.length}
              />
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default TicketsRequest;
